import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import Header from "./components/Header";
import { ScrollToTop } from "./components/ScrollToTop";
import Footer from "./components/Footer";

// Eager load homepage for LCP
import Index from "./pages/Index";

// Lazy load other routes for bundle splitting
const Developments = lazy(() => import("./pages/Developments"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const AssetClasses = lazy(() => import("./pages/AssetClasses"));
const HowWeWork = lazy(() => import("./pages/HowWeWork"));
const About = lazy(() => import("./pages/About"));
const ProfileDetail = lazy(() => import("./pages/ProfileDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const Insights = lazy(() => import("./pages/Insights"));
const InsightDetail = lazy(() => import("./pages/InsightDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const AustralNSW = lazy(() => import("./pages/locations/AustralNSW"));
const CrescentHeadNSW = lazy(() => import("./pages/locations/CrescentHeadNSW"));
const StLeonardsNSW = lazy(() => import("./pages/locations/StLeonardsNSW"));
const HallidaysPointNSW = lazy(() => import("./pages/locations/HallidaysPointNSW"));
const LandForSaleAustral = lazy(() => import("./pages/search/LandForSaleAustral"));
const CrescentHeadLandForSale = lazy(() => import("./pages/search/CrescentHeadLandForSale"));
const StLeonardsPropertyDevelopment = lazy(() => import("./pages/search/StLeonardsPropertyDevelopment"));
const CoastalLandInvestmentNSW = lazy(() => import("./pages/search/CoastalLandInvestmentNSW"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Header />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/developments" element={<Developments />} />
            <Route path="/developments/:slug" element={<ProjectDetail />} />
            <Route path="/projects/:slug" element={<ProjectDetail />} />
            {/* Legacy slug redirects */}
            <Route path="/developments/akira-austral" element={<Navigate to="/projects/akira" replace />} />
            <Route path="/developments/st-leonards" element={<Navigate to="/projects/marco" replace />} />
            <Route path="/developments/crescent-head" element={<Navigate to="/projects/the-crescent" replace />} />
            <Route path="/asset-classes" element={<AssetClasses />} />
            <Route path="/how-we-work" element={<HowWeWork />} />
            <Route path="/about" element={<About />} />
            <Route path="/about/:person" element={<ProfileDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<InsightDetail />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            {/* Location & search page redirects */}
            <Route path="/locations/austral-nsw" element={<Navigate to="/projects/akira" replace />} />
            <Route path="/locations/crescent-head-nsw" element={<Navigate to="/projects/the-crescent" replace />} />
            <Route path="/locations/st-leonards-nsw" element={<Navigate to="/projects/marco" replace />} />
            <Route path="/locations/hallidays-point-nsw" element={<Navigate to="/projects/hallidays-point" replace />} />
            <Route path="/land-for-sale-austral-nsw" element={<Navigate to="/projects/akira" replace />} />
            <Route path="/crescent-head-land-for-sale" element={<Navigate to="/projects/the-crescent" replace />} />
            <Route path="/st-leonards-property-development" element={<Navigate to="/projects/marco" replace />} />
            <Route path="/coastal-land-investment-nsw" element={<Navigate to="/projects/the-crescent" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <Footer />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
